import React, { createRef, ReactElement } from 'react'
import classNames from 'classnames'
import { mergeRefs } from '../../utils/helpers'

export enum CheckboxType {
  radio = 'radio',
  checkbox = 'checkbox',
}

interface IDefaultProps {
  className?: string
  type: CheckboxType
  selected?: string | string[] | null | boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
  inputView?: boolean
}

interface IProps extends Partial<IDefaultProps> {
  value: any // string | boolean
  name: string
  title: string | ReactElement
  subtitle?: string
  icon?: ReactElement
}

const defaultProps: IDefaultProps = {
  type: CheckboxType.checkbox,
  selected: null,
  inputView: true,
}

const CheckboxItem = React.forwardRef(
  (
    {
      className,
      type,
      selected,
      value,
      name,
      onChange,
      disabled,
      title,
      subtitle,
      icon,
      inputView,
    }: IProps,
    ref,
  ) => {
    const localInputRef = createRef<HTMLInputElement>()

    let isSelected = false
    if (Array.isArray(selected) && type === 'checkbox' && value) {
      isSelected = selected.includes(value)
    }
    if (type === 'radio') {
      isSelected = selected === value
    }

    const handleOnClick = () => {
      localInputRef?.current?.click()
    }

    return (
      <div
        onClick={handleOnClick}
        className={classNames(
          className,
          'relative flex items-start cursor-pointer',
          {
            ['p-4']: inputView,
          },
          {
            ['my-1 mr-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none']:
              !inputView,
          },
          { ['border-gray-800 ring-1 ring-gray-800']: isSelected },
          { ['cursor-not-allowed']: disabled },
        )}
      >
        <div className={`flex h-5 items-center ${inputView ? '' : 'sr-only'}`}>
          <input
            className={classNames(
              'mr-3 h-4 w-4 rounded border-gray-300 text-gray-600 disabled:accent-gray-300 focus:ring-gray-500 cursor-pointer',
              selected
                ? 'focus:ring-gray-800 accent-primary-800'
                : 'accent-gray-300 ',
            )}
            onChange={onChange}
            onClick={handleOnClick}
            ref={mergeRefs(ref, localInputRef)}
            value={value}
            type={type}
            name={name}
            disabled={disabled}
          />
        </div>
        <div
          className={classNames(
            'min-w-0 flex-1 text-sm sm:flex sm:items-center sm:justify-start',
            {
              ['']: isSelected,
            },
          )}
        >
          {icon && (
            <div className='flex-shrink-0 -ml-2 pr-3'>
              <div
                className={classNames(
                  { ['ring ring-2 ring-offset-1']: isSelected },
                  'h-10 w-10 bg-gray-100 ml-1 relative p-1.5 ring-gray-100 rounded-full flex items-center justify-center cursor-pointer focus:outline-none',
                )}
              >
                {icon}
              </div>
            </div>
          )}
          <div className='mt-0 text-sm flex flex-col'>
            <span
              className={classNames(
                'font-medium text-gray-700 select-none text-sm',
                {
                  ['font-bold text-gray-900']: isSelected,
                },
              )}
            >
              {title}
            </span>
            {subtitle && (
              <span
                className={classNames(
                  'font-normal text-gray-500 text-sm leading-5',
                  {
                    ['text-gray-600']: isSelected,
                  },
                )}
              >
                <span className='block sm:inline '>{subtitle}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    )
  },
)

CheckboxItem.defaultProps = defaultProps

export { CheckboxItem }
